// Written by Claude Code on 2026-02-04
// User prompt: Implement the following plan: Role-Based Dashboards
// Updated by Claude Code on 2026-03-03
// User prompt: Implement role-based auth with database User table

// Written by Codex on 2026-10-15
// User prompt: demo readiness
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import api from '../services/api';

const UserContext = createContext(undefined);

export function UserProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userError, setUserError] = useState(null);
  const currentUser = useRef(null);
  const request = useRef(null);
  const generation = useRef(0);

  const invalidate = useCallback(() => {
    generation.current += 1;
    request.current?.abort();
    request.current = null;
  }, []);

  const loadProfile = useCallback(async (user) => {
    if (!user || request.current) return;
    const controller = new AbortController();
    const version = generation.current;
    request.current = controller;
    setLoading(true); setUserError(null); setDbUser(null);
    let timer;
    const isCurrent = () => version === generation.current && currentUser.current?.uid === user.uid;
    try {
      const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
          reject(Object.assign(new Error('Profile check timed out'), { code: 'PROFILE_TIMEOUT' }));
          controller.abort();
        }, 15000);
      });
      const res = await Promise.race([api.get('/users/me', { signal: controller.signal, timeout: 15000 }), timeout]);
      if (isCurrent()) setDbUser(res.data);
    } catch (err) {
      if (!isCurrent()) return;
      const status = err.response?.status;
      setUserError(['PROFILE_TIMEOUT', 'ECONNABORTED', 'ETIMEDOUT'].includes(err.code)
        ? 'Loading your profile took too long. Check your connection and select Retry profile.'
        : status === 401 ? 'Your sign-in could not be verified. Retry your profile, or sign out and sign in again.'
        : status === 403 ? (err.response?.data?.detail || 'Your account does not have access to this organization. Contact your administrator.')
        : !err.response ? 'We could not connect to load your profile. Check your connection and select Retry profile.'
        : 'Your profile is temporarily unavailable. Select Retry profile to try again.');
    } finally {
      clearTimeout(timer);
      if (isCurrent()) { request.current = null; setLoading(false); }
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      invalidate();
      currentUser.current = user;
      setFirebaseUser(user);
      setDbUser(null);
      setUserError(null);
      if (!user) {
        setLoading(false);
        return;
      }
      loadProfile(user);
    });
    return () => { unsubscribe(); invalidate(); currentUser.current = null; };
  }, [invalidate, loadProfile]);

  const logout = async () => {
    invalidate();
    currentUser.current = null;
    setDbUser(null);
    setLoading(false);
    try { await signOut(auth); }
    catch {
      currentUser.current = auth.currentUser;
      setUserError('Sign-out could not be completed. Retry your profile or try signing out again.');
    }
  };

  const refreshUser = () => loadProfile(currentUser.current);

  // Role from DB (employee, manager, hr). Map hr to match frontend nav keys.
  const role = dbUser?.role || null;

  // For components that expect mockUserData shape (e.g. useFilteredLeaves)
  const mockUserData = dbUser
    ? {
        userId: dbUser.id,
        name: dbUser.name || dbUser.email?.split('@')[0] || 'User',
        teamIds: role === 'manager' ? [] : undefined,
      }
    : null;

  const value = {
    firebaseUser,
    dbUser,
    loading,
    role,
    mockUserData,
    userError,
    logout,
    refreshUser,
    retryProfile: refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
